import {
    BehaviorSubject,
    Observable,
    catchError,
    combineLatest,
    distinctUntilChanged,
    filter,
    map,
    of,
    shareReplay,
    skip,
    startWith,
    switchMap,
    take,
    timer,
} from "rxjs";
import {
    BillingStateDoc,
    PlatformAlertDoc,
    PlatformAlertFrequency,
    PlatformAlertRoleKey,
    PlatformAlertView,
} from "../../model/platform-alert.interface";
import { DestroyRef, Injectable, inject } from "@angular/core";
import {
    Firestore,
    Timestamp,
    doc,
    docData,
    serverTimestamp,
} from "@angular/fire/firestore";
import { NavigationEnd, Router } from "@angular/router";
import { Role, User, UserPreferences } from "../../model/user.model";

import { AlertType } from "../../model/alert";
import { AuthService } from "../auth.service";
import { TranslocoService } from "@jsverse/transloco";
import { UserService } from "../user.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

/**
 * Single place for the frontend's billingBlockLevel -> behavior rules:
 * each entry is the minimum level at which that behavior kicks in, for every role.
 * Banner presentation per level lives in BILLING_LEVEL_ALERT_CONFIG below.
 */
export const BILLING_LEVEL_RULES = {
    campaignsBlocked: 2,
    platformBlocked: 4,
} as const;

function reaches(level: number | undefined, threshold: number): boolean {
    return level !== undefined && level >= threshold;
}

/** Only these billing levels get a banner: 0 means no restriction, and from
 * BILLING_LEVEL_RULES.platformBlocked on there is a full-screen block instead. */
type BillingLevelWithAlert = 1 | 2 | 3;

function isBillingLevelWithAlert(
    level: number | undefined,
): level is BillingLevelWithAlert {
    return level === 1 || level === 2 || level === 3;
}

/**
 * Single place to tune how each billing level is presented. Change `type`,
 * `closeable` or `onNavigation` here without touching the derivation logic below.
 * With `onNavigation`, closing only hides the alert until the next route change.
 */
export const BILLING_LEVEL_ALERT_CONFIG: Partial<
    Record<
        BillingLevelWithAlert,
        { type: AlertType; closeable: boolean; onNavigation: boolean }
    >
> = {
    1: { type: AlertType.WARNING, closeable: true, onNavigation: true },
    2: { type: AlertType.ERROR, closeable: true, onNavigation: true },
    3: { type: AlertType.ERROR, closeable: true, onNavigation: true },
};

/**
 * Material icon per alert type. AlertComponent only draws an icon when one is
 * passed explicitly, so the banner resolves it here instead of in the component.
 */
export const ALERT_TYPE_ICON: Record<AlertType, string> = {
    [AlertType.WARNING]: "warning",
    [AlertType.ERROR]: "error",
    [AlertType.INFO]: "info",
    [AlertType.SUCCESS]: "check_circle",
};

/**
 * The billing alert is only visible to admin and superAdmin — same pattern as
 * AuthService.validateAccess.
 * This is purely a visibility filter; it adds no operational restriction.
 * Levels 2+ will get their own blocking guards independently of who can see
 * the alert.
 */
export const BILLING_ALERT_VISIBLE_ROLES: readonly Role[] = [
    Role.admin,
    Role.superAdmin,
];

export function canSeeBillingAlert(role: Role): boolean {
    return BILLING_ALERT_VISIBLE_ROLES.includes(role);
}

/**
 * Only decides anything for a level configured with `onNavigation: false`; while
 * every level uses `onNavigation`, closing never persists and this is inert.
 *
 * "per_session", not "once": the alert id is scoped per activation (see the
 * epoch suffix in fromBilling), so dismissing it only ever hides that one
 * episode — "once" would still be correct for that. "per_session" additionally
 * re-warns the admin on their next login even if the same episode is still
 * ongoing, instead of staying silently dismissed for as long as the browser
 * session (and persisted auth) happen to live.
 */
const BILLING_ALERT_FREQUENCY: PlatformAlertFrequency = "per_session";

/**
 * Minimum billing block level at which campaign creation is rejected, for every role.
 * Mirrors `CAMPAIGN_CREATE_BILLING_BLOCK_LEVEL` in atom-cloudfunctions'
 * CampaignController — the two repos don't share a types package, so the name is kept
 * for grepping both repos when the threshold changes; the value lives in
 * BILLING_LEVEL_RULES.
 */
export const CAMPAIGN_CREATE_BILLING_BLOCK_LEVEL =
    BILLING_LEVEL_RULES.campaignsBlocked;

export function isCampaignCreationBlockedLevel(
    level: number | undefined,
): boolean {
    return reaches(level, BILLING_LEVEL_RULES.campaignsBlocked);
}

/**
 * Which billing level (if any) the given user should see an alert for: null when the
 * level has no configured alert, or when the user's role isn't allowed to see billing
 * alerts.
 */
export function resolveBillingAlertLevel(
    billing: BillingStateDoc | undefined,
    user: User,
): BillingLevelWithAlert | null {
    const role = user.rol;
    if (!role || !canSeeBillingAlert(role)) return null;

    const level = billing?.billingBlockLevel;
    if (!isBillingLevelWithAlert(level)) return null;
    if (!BILLING_LEVEL_ALERT_CONFIG[level]) return null;
    return level;
}

export function isTotalBlockLevel(level: number | undefined): boolean {
    return reaches(level, BILLING_LEVEL_RULES.platformBlocked);
}

/**
 * superAdmin is never totally blocked: they are the platform administrator the
 * blocked page points to, and their userdoc companyId goes stale when switching
 * companies, so blocking them could trap them in a blocked company.
 */
export function canBeTotallyBlocked(
    user: User | null | undefined,
): user is User & { companyId: string } {
    return Boolean(user?.companyId) && user?.rol !== Role.superAdmin;
}

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

/** How often to re-evaluate dismissal while the tab stays open, so a `daily`
 * alert reappears at the 24h mark without waiting for another Firestore
 * emission or a page reload. */
const REEVALUATE_INTERVAL_MS = 60 * 1000;

/**
 * Whether the user's stored dismissal still hides `alert`.
 * Only one dismissal is stored: it applies when it targets the current alert id.
 * Never for `onNavigation` alerts: their dismissal lives in memory only, so a stale
 * stored dismissal with the same id must not hide them.
 */
export function isDismissed(
    alert: PlatformAlertView,
    preferences:
        | Pick<UserPreferences, "dismissedAlertId" | "dismissedAt">
        | undefined,
    lastSignInMs: number | undefined,
    nowMs: number,
): boolean {
    if (!alert.closeable) return false;
    if (alert.onNavigation) return false;
    if (preferences?.dismissedAlertId !== alert.id) return false;

    // Null while the serverTimestamp() write is still pending: it was just closed.
    const dismissedAt =
        preferences.dismissedAt instanceof Timestamp
            ? preferences.dismissedAt.toMillis()
            : undefined;
    if (dismissedAt === undefined) return true;

    switch (alert.frequency) {
        case "once":
            return true;
        case "daily":
            return nowMs - dismissedAt <= ONE_DAY_MS;
        case "per_session":
            return lastSignInMs === undefined || dismissedAt >= lastSignInMs;
        default:
            // Backend default (billing-block.schemas.ts) is per_session; an
            // unrecognized/missing frequency falls back to that semantics
            // instead of `undefined` (falsy), which would make a closeable
            // alert impossible to dismiss for good.
            return lastSignInMs === undefined || dismissedAt >= lastSignInMs;
    }
}

/** Whether `alert` can go from dismissed to visible with no new emission (time alone). */
export function needsTimedReevaluation(alert: PlatformAlertView): boolean {
    return (
        alert.closeable && !alert.onNavigation && alert.frequency === "daily"
    );
}

/**
 * Value equality for alert$ emissions, so a recomputation that yields the same alert
 * (a user doc update, a timer tick) doesn't hand the banner a new reference.
 */
export function isSameAlertView(
    a: PlatformAlertView | null,
    b: PlatformAlertView | null,
): boolean {
    if (a === b) return true;
    if (!a || !b) return false;
    return (
        a.id === b.id &&
        a.source === b.source &&
        a.type === b.type &&
        a.icon === b.icon &&
        a.title === b.title &&
        a.description === b.description &&
        a.closeable === b.closeable &&
        a.frequency === b.frequency &&
        a.onNavigation === b.onNavigation &&
        a.level === b.level
    );
}

/**
 * Text in `lang`, falling back to Spanish and then to the first available
 * language. Non-string values are skipped, so a document written with another
 * shape can never reach the banner as `[object Object]`.
 */
export function resolveLocalizedText(
    texts: Record<string, unknown> | undefined,
    lang: string,
): string | undefined {
    const candidates = [
        texts?.[lang],
        texts?.es,
        ...Object.values(texts ?? {}),
    ];
    return candidates.find(
        (text): text is string => typeof text === "string" && text !== "",
    );
}

const ROLE_TO_KEY: Record<string, PlatformAlertRoleKey> = {
    [Role.admin]: "admin",
    [Role.superAdmin]: "superadmin",
    [Role.coach]: "supervisor",
    [Role.agent]: "agent",
};

/**
 * Translates billingState/current and platformAlerts/current into the inputs
 * of AlertComponent. Independent from the Mi Suscripción alerts engine.
 */
@Injectable({ providedIn: "root" })
export class PlatformAlertsService {
    private readonly afs = inject(Firestore);
    private readonly authService = inject(AuthService);
    private readonly userService = inject(UserService);
    private readonly transloco = inject(TranslocoService);
    private readonly router = inject(Router);
    private readonly destroyRef = inject(DestroyRef);

    /**
     * One shared billingState/current listener per company: alert$, totalBlock$, the
     * campaign-creation guard and the campaign screens all reuse it instead of opening
     * their own. Each entry drops its listener when its last subscriber leaves.
     */
    /**
     * Dismissal made in this tab, applied right away. The persisted one only reaches
     * alert$ when the user doc listener emits, and for superAdmin it never does
     * (userRealTime isn't fed for them). Goes through the same isDismissed() rules,
     * so `closeable` and `frequency` keep deciding what can be hidden and for how long.
     */
    private readonly localDismissal$ = new BehaviorSubject<
        | {
              userId: string;
              preferences: Pick<
                  UserPreferences,
                  "dismissedAlertId" | "dismissedAt"
              >;
          }
        | undefined
    >(undefined);

    /**
     * Id of the `onNavigation` alert closed in this tab, until the route path changes.
     * Memory only: a reload brings the alert back, and nothing is written to Firestore.
     */
    private readonly navigationDismissal$ = new BehaviorSubject<string | null>(
        null,
    );

    private readonly billingStateByCompany = new Map<
        string,
        Observable<BillingStateDoc | undefined>
    >();

    /**
     * Company whose total block applies to the logged-in user, or null when there is
     * none. Based on userdoc, which every role gets at login, so totalBlock$ also works
     * outside the core layout (business-setup, talkdesk), where the current company is
     * never published. superAdmin maps to null: their userdoc companyId goes stale when
     * switching companies (see canBeTotallyBlocked).
     */
    private readonly billingCompanyId$: Observable<string | null> =
        this.authService.userdoc.pipe(
            map((user) => (canBeTotallyBlocked(user) ? user.companyId : null)),
            distinctUntilChanged(),
        );

    /**
     * Company currently open in the app, for every role. Unlike billingCompanyId$,
     * superAdmin is included and follows the company they switch to (CoreComponent
     * publishes it through AuthService.setCompany), so billing alerts and operational
     * restrictions apply to the company a superAdmin is actually viewing.
     */
    readonly currentCompanyId$: Observable<string | null> = this.authService
        .getCompany()
        .pipe(
            map((company) => company?.id ?? null),
            distinctUntilChanged(),
        );

    /**
     * Alert to display for the logged-in user, or null when there is none.
     * Shared: every subscriber reuses the same Firestore listeners, which are
     * released when the last one unsubscribes. Follows currentCompanyId$, not
     * billingCompanyId$: superAdmin is one of BILLING_ALERT_VISIBLE_ROLES, and only
     * the total block (totalBlock$) excludes them.
     */
    readonly alert$: Observable<PlatformAlertView | null> =
        this.currentCompanyId$.pipe(
            switchMap((companyId) =>
                companyId ? this.buildAlert$(companyId) : of(null),
            ),
            shareReplay({ bufferSize: 1, refCount: true }),
        );

    /**
     * Live: whether the logged-in user's company is totally blocked (level 4+). Keeps
     * emitting as billingState/current changes, so an open session can react without
     * waiting for its next navigation. Always false with no user, no company, or for
     * superAdmin.
     */
    readonly totalBlock$: Observable<boolean> = this.billingCompanyId$.pipe(
        switchMap((companyId) =>
            companyId
                ? this.billingLevel$(companyId).pipe(map(isTotalBlockLevel))
                : of(false),
        ),
        distinctUntilChanged(),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    /**
     * Live: whether campaign creation is blocked for the company currently open, for
     * every role (unlike `alert$`, which admin/superAdmin only see). Shared, so the
     * campaign screens don't each open their own listener.
     */
    readonly campaignCreationBlocked$: Observable<boolean> =
        this.currentCompanyId$.pipe(
            switchMap((companyId) =>
                companyId
                    ? this.isCampaignCreationBlocked$(companyId)
                    : of(false),
            ),
            distinctUntilChanged(),
            shareReplay({ bufferSize: 1, refCount: true }),
        );

    constructor() {
        // Eager on purpose: hooked to alert$ instead, navigations made while the
        // banner isn't mounted would be missed. Query params and fragment don't
        // count as a route change; startWith + skip(1) keeps a navigation to the
        // current path from clearing the dismissal.
        const pathOf = (url: string): string => url.split(/[?#]/)[0];
        this.router.events
            .pipe(
                filter(
                    (event): event is NavigationEnd =>
                        event instanceof NavigationEnd,
                ),
                map((event) => pathOf(event.urlAfterRedirects)),
                startWith(pathOf(this.router.url)),
                distinctUntilChanged(),
                skip(1),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(() => this.navigationDismissal$.next(null));
    }

    /**
     * Whether campaign creation should be blocked for `companyId`, for every role. Single
     * place for the frontend's creation-block threshold, so callers never hardcode
     * `level >= N` themselves.
     */
    isCampaignCreationBlocked$(companyId: string): Observable<boolean> {
        return this.billingLevel$(companyId).pipe(
            map(isCampaignCreationBlockedLevel),
        );
    }

    dismiss(alert: PlatformAlertView): Observable<boolean> {
        if (!alert.closeable) return of(false);
        // Hidden until the next route change; leaves the stored preferences (and
        // another alert's dismissedAlertId) untouched.
        if (alert.onNavigation) {
            this.navigationDismissal$.next(alert.id);
            return of(true);
        }

        // userRealTime is never fed for superAdmin; userdoc always is.
        const user$ = this.authService.userRealTime.value
            ? of(this.authService.userRealTime.value)
            : this.authService.userdoc.pipe(take(1));

        return user$.pipe(
            switchMap((user) => {
                const isSuperAdmin = user?.rol === Role.superAdmin;
                if (!user?.id || (!isSuperAdmin && !user.companyId)) {
                    return of(false);
                }
                this.localDismissal$.next({
                    userId: user.id,
                    preferences: {
                        dismissedAlertId: alert.id,
                        dismissedAt: Timestamp.now(),
                    },
                });
                return this.userService.updateUserPreferences(
                    user.companyId,
                    user.id,
                    {
                        dismissedAlertId: alert.id,
                        dismissedAt: serverTimestamp(),
                    },
                    isSuperAdmin,
                );
            }),
        );
    }

    private buildAlert$(
        companyId: string,
    ): Observable<PlatformAlertView | null> {
        const billing$ = this.billingState$(companyId);
        const platform$ = this.listen<PlatformAlertDoc>(
            `companies/${companyId}/platformAlerts/current`,
        );
        // userRealTime keeps preferences live (dismissals), but it is never fed for
        // superAdmin; fall back to userdoc so their alert stream still emits.
        const user$ = this.authService.userRealTime.pipe(
            switchMap((user) => (user ? of(user) : this.authService.userdoc)),
            filter((user): user is User => !!user),
        );
        // Firebase Auth only refreshes lastSignInTime on an explicit login, not
        // on a token refresh or on reopening the browser with a persisted
        // session — so for most users `per_session` will behave like `once`
        // across weeks of the same browser session. Chosen deliberately to avoid
        // inventing a session concept of our own (no sessionStorage). If Product
        // confirms "new session" should mean "new visit" instead of "explicit
        // login", this is the place to swap in a sessionStorage-backed session id.
        const lastSignIn$ = this.authService.user.pipe(
            map((authUser) =>
                Date.parse(authUser?.metadata?.lastSignInTime ?? ""),
            ),
            map((ms) => (Number.isNaN(ms) ? undefined : ms)),
            startWith(undefined),
        );
        const lang$ = this.transloco.langChanges$.pipe(
            startWith(this.transloco.getActiveLang()),
        );

        return combineLatest([
            billing$,
            platform$,
            user$,
            lastSignIn$,
            lang$,
            this.localDismissal$,
            this.navigationDismissal$,
        ]).pipe(
            switchMap(
                ([
                    billing,
                    platform,
                    user,
                    lastSignIn,
                    lang,
                    localDismissal,
                    navigationDismissal,
                ]) => {
                    const candidate = this.pickAlert(
                        billing,
                        platform,
                        user,
                        lang,
                    );
                    if (!candidate) return of(null);
                    if (
                        candidate.onNavigation &&
                        navigationDismissal === candidate.id
                    ) {
                        return of(null);
                    }
                    const visible = (): PlatformAlertView | null => {
                        const nowMs = Date.now();
                        const dismissed =
                            isDismissed(
                                candidate,
                                user.preferences,
                                lastSignIn,
                                nowMs,
                            ) ||
                            (localDismissal?.userId === user.id &&
                                isDismissed(
                                    candidate,
                                    localDismissal.preferences,
                                    lastSignIn,
                                    nowMs,
                                ));
                        return dismissed ? null : candidate;
                    };
                    // Only a dismissed `daily` alert can reappear with time alone; the rest
                    // only change with an emission above, so they need no timer.
                    return needsTimedReevaluation(candidate)
                        ? timer(0, REEVALUATE_INTERVAL_MS).pipe(map(visible))
                        : of(visible());
                },
            ),
            distinctUntilChanged(isSameAlertView),
        );
    }

    /** Billing wins over the generic alert; precedence ignores dismissal state. */
    private pickAlert(
        billing: BillingStateDoc | undefined,
        platform: PlatformAlertDoc | undefined,
        user: User,
        lang: string,
    ): PlatformAlertView | null {
        const billingAlert = this.fromBilling(billing, user);
        if (billingAlert) return billingAlert;
        return this.fromPlatform(platform, user, lang);
    }

    private fromBilling(
        billing: BillingStateDoc | undefined,
        user: User,
    ): PlatformAlertView | null {
        const level = resolveBillingAlertLevel(billing, user);
        if (level === null) return null;
        const config = BILLING_LEVEL_ALERT_CONFIG[level];
        if (!config) return null;

        const key = `platform_alerts.billing.level_${level}`;
        // Suffixed with the write's own timestamp, not just the level: the
        // backend sets billingBlockLevel back to the same level on every
        // reactivation, and without this a dismissal from a past episode
        // would keep matching and hide the new one by id alone.
        const epoch = billing?.updatedAt?.toMillis() ?? "unknown";
        return {
            id: `billing-level-${level}-${epoch}`,
            source: "billing",
            type: config.type,
            icon: ALERT_TYPE_ICON[config.type],
            title: this.transloco.translate(`${key}.title`),
            description: this.transloco.translate(`${key}.description`),
            closeable: config.closeable,
            frequency: BILLING_ALERT_FREQUENCY,
            onNavigation: config.onNavigation,
            level,
        };
    }

    private fromPlatform(
        platform: PlatformAlertDoc | undefined,
        user: User,
        lang: string,
    ): PlatformAlertView | null {
        if (!platform?.active) return null;
        const role = user.rol;
        if (!role) return null;
        const roleKey = ROLE_TO_KEY[role];
        if (!roleKey || !platform.roles?.[roleKey]) return null;

        // Description is required; title is optional and never invalidates the alert.
        const description = resolveLocalizedText(platform.text, lang);
        if (!description) return null;

        // Closeable unless the document explicitly opts out.
        const closeable = platform.closeable ?? true;
        return {
            id: platform.id,
            source: "platform",
            type: platform.type,
            icon: ALERT_TYPE_ICON[platform.type],
            title: resolveLocalizedText(platform.title, lang),
            description,
            closeable,
            frequency: platform.frequency,
            // Only an explicit true: documents written keep the
            // original `frequency` behavior.
            onNavigation: closeable && platform.onNavigation === true,
        };
    }

    /**
     * Raw billingBlockLevel for `companyId`, with no role filter applied. Unlike `alert$`
     * (admin/superAdmin only), operational restrictions such as the campaign-creation
     * guard apply to every role and need the unfiltered level.
     */
    private billingLevel$(companyId: string): Observable<number | undefined> {
        return this.billingState$(companyId).pipe(
            map((billing) => billing?.billingBlockLevel),
        );
    }

    private billingState$(
        companyId: string,
    ): Observable<BillingStateDoc | undefined> {
        let state$ = this.billingStateByCompany.get(companyId);
        if (!state$) {
            state$ = (
                docData(
                    doc(
                        this.afs,
                        `companies/${companyId}/billingState/current`,
                    ),
                ) as Observable<BillingStateDoc | undefined>
            ).pipe(shareReplay({ bufferSize: 1, refCount: true }));
            this.billingStateByCompany.set(companyId, state$);
        }
        return state$.pipe(catchError(() => of(undefined)));
    }

    /** A listener error (e.g. permission denied) degrades to "no document", so no banner is shown. */
    private listen<T>(path: string): Observable<T | undefined> {
        return (docData(doc(this.afs, path)) as Observable<T | undefined>).pipe(
            catchError(() => of(undefined)),
        );
    }
}
